import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { injectIntl, FormattedMessage } from "react-intl";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import { PaginatedTable, HeaderCell, TableIconCell } from "components/table";
import I18nBoolean from "components/i18n-boolean";
import IconButton from "components/icon-button";
import Icon from "components/icon";
import FormConfirm from "components/form/form-confirm";
import { useImperativeTracking } from "components/tracking";
import { manageComponentService } from "../../../../services/commonService";
import { DeleteActionWithModal } from "components/buttons";

// Adaptador para que las llamadas REST de manage-component encajen con el contrato que esperan
// `doAction`/`DeleteActionWithModal`: una función (options) => Promise que resuelve con
// { data: { [elementNameInResult]: true } } en éxito.
const manageComponentAction = (action, elementNameInResult) => ({ variables: { eventId, componentName } }) =>
  new Promise((resolve, reject) =>
    manageComponentService(
      eventId,
      componentName,
      action,
      () => resolve({ data: { [elementNameInResult]: true } }),
      reject,
    ),
  );

const startComponent = manageComponentAction("start", "eventStartComponent");
const stopComponent = manageComponentAction("stop", "eventStopComponent");
const deleteComponent = manageComponentAction("remove", "eventDeleteComponent");

// El backend tarda un instante en reflejar el nuevo estado: se refresca ya y de nuevo poco después.
const REFRESH_DELAY_MS = 2000;

const doAction = ({ action, actionName, item, eventId, track, setSuccessConfirm, setShowConfirm, refreshEvent }) => {
  track(`User ${actionName} component [${item.name}] for event [${eventId}]`);
  return action({ variables: { eventId, componentName: item.name } })
    .then(result => {
      setSuccessConfirm(true);
      refreshEvent();
      setTimeout(refreshEvent, REFRESH_DELAY_MS);
      return result;
    })
    .catch(error => {
      console.error(error);
      setSuccessConfirm(false);
    })
    .finally(() => {
      setShowConfirm(true);
    });
};

const Header = () => (
  <TableRow>
    <HeaderCell>
      <FormattedMessage id="event.components.name" />
    </HeaderCell>
    <HeaderCell>
      <FormattedMessage id="event.components.autorun" />
    </HeaderCell>
    <HeaderCell>
      <FormattedMessage id="event.components.status" />
    </HeaderCell>
    <HeaderCell />
  </TableRow>
);

const Row = ({ eventId, setShowConfirm, setSuccessConfirm, refreshEvent, item, classes }) => {
  const track = useImperativeTracking();

  const handleStart = () =>
    doAction({
      action: startComponent,
      actionName: "started",
      item,
      eventId,
      track,
      setSuccessConfirm,
      setShowConfirm,
      refreshEvent,
    });

  const handleStop = () =>
    doAction({
      action: stopComponent,
      actionName: "stopped",
      item,
      eventId,
      track,
      setSuccessConfirm,
      setShowConfirm,
      refreshEvent,
    });

  return (
    <TableRow key={item.name}>
      <TableCell>{item.name}</TableCell>
      <TableCell>
        <I18nBoolean value={item.autorun} />
      </TableCell>
      <TableCell>
        {item.status ? <FormattedMessage id={`event.components.status.in.${item.status}`} /> : null}
      </TableCell>
      <TableIconCell>
        {item.status === "stopped" && (
          <IconButton onClick={handleStart}>
            <Icon name="play-arrow" />
          </IconButton>
        )}
        {item.status !== "stopped" && (
          <IconButton onClick={handleStop}>
            <Icon name="stop" />
          </IconButton>
        )}
        <DeleteActionWithModal
          showLoading={false}
          mutation={deleteComponent}
          mutationVariables={{
            eventId,
            componentName: item.name,
          }}
          elementToDelete={item.name}
          elementNameInResult="eventDeleteComponent"
          onSuccess={() => {
            setSuccessConfirm(true);
            setShowConfirm(true);
            refreshEvent();
          }}
          onError={error => {
            console.error(error);
            setSuccessConfirm(false);
            setShowConfirm(true);
          }}
        />
      </TableIconCell>
    </TableRow>
  );
};

Row.propTypes = {
  /**
   ** Componente
   **/
  item: PropTypes.object,
  /**
   ** Id del evento
   **/
  eventId: PropTypes.string,
  /**
   ** Indica si se muestra el mensaje de feedback
   **/
  setShowConfirm: PropTypes.func,
  /**
   ** Indica si el mensaje de feedback es de éxito o no
   **/
  setSuccessConfirm: PropTypes.func,
  /**
   ** Fuerza una recarga inmediata del evento
   **/
  refreshEvent: PropTypes.func,
};

const ComponentStatusTab = ({ componentStatus, eventId, refreshEvent }) => {
  const [show, setShow] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleCloseConfirm = useCallback(() => setShow(false), []);

  return (
    <>
      <FormConfirm
        show={show}
        error={!success}
        success={success}
        onClose={handleCloseConfirm}
        hideCloseButton={false}
        autoHideDuration={2000}
        label={<FormattedMessage id={`event.components.action.${success ? "success" : "error"}`} />}
      />
      <PaginatedTable
        paginatedElements={{ items: componentStatus, total: componentStatus.length }}
        headerComponent={Header}
        rowComponent={Row}
        showPagination={false}
        perPage={0}
        rowProps={{ eventId, setShowConfirm: setShow, setSuccessConfirm: setSuccess, refreshEvent }}
      />
    </>
  );
};

ComponentStatusTab.propTypes = {
  /**
   ** Componentes de la sala
   **/
  componentStatus: PropTypes.arrayOf(PropTypes.object),
  /**
   ** Id del evento
   **/
  eventId: PropTypes.string,
  /**
   ** Fuerza una recarga inmediata del evento
   **/
  refreshEvent: PropTypes.func,
};

export default injectIntl(ComponentStatusTab);
